import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight, Box, Check, CircleDollarSign, ClipboardList, Clock3,
    Edit2, Eye, Package, Pencil, RefreshCw, Rocket, ShoppingBag, Sparkles, Trophy,
} from "lucide-react";
import {useEditor} from "../../../../App.jsx";
import {editorPathForId} from "../../../../products/registry";

const TYPE_ICON = {
    design: Box,
    request: ClipboardList,
    order: Package,
};

const formatDate = (value) => {
    if (!value) return "";
    return new Intl.DateTimeFormat("es-ES", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    }).format(new Date(value));
};

const formatCurrency = (value, currency = "EUR") =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency }).format(Number(value || 0));

const getDesignPreview = (design) => {
    if (design.thumbnail) return design.thumbnail;
    if (!design.svg) return null;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(design.svg)}`;
};

const StatCard = ({ icon: Icon, label, value, hint, color }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glassCard rounded-2xl p-4 min-w-0"
    >
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-base-content/50">{label}</p>
                <p className="mt-2 text-3xl font-black text-base-content truncate">{value}</p>
                <p className="mt-1 text-xs text-base-content/50">{hint}</p>
            </div>
            <span className={`rounded-xl p-2.5 ${color}`}><Icon size={20} /></span>
        </div>
    </motion.div>
);

const DashboardSkeleton = () => (
    <div className="space-y-4 animate-pulse">
        <div className="h-52 rounded-2xl bg-base-300/70" />
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, index) => <div key={index} className="h-28 rounded-2xl bg-base-300/70" />)}
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 h-72 rounded-2xl bg-base-300/70" />
            <div className="h-72 rounded-2xl bg-base-300/70" />
        </div>
    </div>
);

export default function ClientDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get("/dashboard/client-stats/");
            setData(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || "No se pudo cargar tu resumen.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

    const unlocked = useMemo(
        () => data?.milestones?.filter((milestone) => milestone.unlocked).length || 0,
        [data],
    );

    if (loading) return <DashboardSkeleton />;

    if (error || !data) {
        return (
            <div className="glassCard rounded-2xl min-h-80 flex flex-col items-center justify-center gap-4 p-8 text-center">
                <RefreshCw size={34} className="text-error" />
                <div>
                    <h2 className="text-xl font-bold text-base-content">Tu panel no ha podido cargar</h2>
                    <p className="mt-1 text-sm text-base-content/60">{error}</p>
                </div>
                <button className="btn btn-primary btn-sm gap-2" onClick={fetchDashboard}>
                    <RefreshCw size={15} /> Reintentar
                </button>
            </div>
        );
    }

    const firstName = data.user.name?.trim().split(/\s+/)[0] || "creador";

        const { stateEditor, setStateEditor } = useEditor();

    const handleProfile = () => {
        setStateEditor({
            ...stateEditor,
            editor: true,
            editorID: null,
            editorType: "profileUser",
        });
    };
    return (
        <div className="space-y-4 pb-4">
            <section className="relative overflow-hidden rounded-2xl bg-neutral text-neutral-content p-6 md:p-8">
                <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-primary-content/10 blur-2xl" />
                <div className="absolute bottom-0 right-1/4 h-28 w-28 rounded-full bg-accent/20 blur-2xl" />
                <div className="relative grid lg:grid-cols-[1fr_auto] gap-6 items-center">
                    <div>
                        <div className="flex items-center gap-2 text-sm text-neutral-content/70">
                            <Sparkles size={15} /> Tu espacio creativo
                        </div>

                        <h1 className="mt-2 text-3xl md:text-4xl font-black text-neutral-content">
                            Hola, {firstName}
                        </h1>
                        <p className="mt-2 max-w-2xl text-sm md:text-base text-neutral-content/70">
                            Diseña, solicita fabricación y sigue cada pieza desde un único lugar.
                        </p>
                        <div className="mt-5 flex flex-wrap gap-2">
                            <Link to="/customizers" className="btn btn-sm bg-white text-black border-white hover:bg-white/90 gap-2">
                                <Rocket size={16} /> Crear diseño
                            </Link>
                            <Link to="/profile/modelos_creados" className="btn btn-sm btn-outline border-white/30 text-white hover:bg-white/10 gap-2">
                                Ver mis diseños <ArrowRight size={15} />
                            </Link>
                            <button
                                className="btn btn-primary btn-sm gap-2 self-start sm:self-center flex-shrink-0"
                                onClick={handleProfile}
                                title="Editar perfil"
                            >
                                <Pencil size={14} />
                                <span className="hidden sm:inline">Editar perfil</span>
                            </button>
                        </div>
                    </div>
                    <div className="w-full lg:w-64 rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-neutral-content/70">Perfil completado</span>
                            <strong className="text-neutral-content">{data.user.profileProgress}%</strong>
                        </div>
                        <progress className="progress progress-accent mt-3 w-full" value={data.user.profileProgress} max="100" />
                        <p className="mt-2 text-xs text-neutral-content/60">
                            Un perfil completo agiliza presupuestos y entregas.
                        </p>
                    </div>
                </div>
            </section>

            <section className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                <StatCard icon={Box} label="Diseños" value={data.stats.designs} hint="creaciones guardadas" color="bg-primary/10 text-primary" />
                <StatCard icon={ClipboardList} label="Solicitudes" value={data.stats.requests} hint={`${data.pipeline.reviewing} en revisión`} color="bg-info/10 text-info" />
                <StatCard icon={Package} label="Pedidos activos" value={data.stats.activeOrders} hint={`${data.stats.delivered} entregados`} color="bg-warning/10 text-warning" />
                <StatCard icon={CircleDollarSign} label="Compras realizadas" value={formatCurrency(data.stats.spent, data.stats.currency)} hint="pedidos confirmados" color="bg-success/10 text-success" />
            </section>

            <section className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 glassCard rounded-2xl p-5 md:p-6">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-base-content/50">Recomendado ahora</p>
                            <h2 className="mt-1 text-xl font-black text-base-content">{data.nextAction.title}</h2>
                        </div>
                        <span className="rounded-xl bg-accent/10 p-3 text-accent"><Sparkles size={22} /></span>
                    </div>
                    <p className="mt-3 text-sm text-base-content/65">{data.nextAction.description}</p>
                    <Link to={data.nextAction.link} className="btn btn-primary btn-sm mt-5 gap-2">
                        {data.nextAction.label} <ArrowRight size={15} />
                    </Link>

                    <div className="mt-6 border-t border-base-300 pt-5">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-base-content">Estado de tus solicitudes</h3>
                            <Link to="/profile/mis-solicitudes" className="text-xs font-semibold text-primary hover:underline">Ver detalle</Link>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {[
                                ["En revisión", data.pipeline.reviewing],
                                ["Con propuesta", data.pipeline.proposals],
                                ["Listas para comprar", data.pipeline.readyToBuy],
                                ["En producción", data.pipeline.inProduction],
                            ].map(([label, value]) => (
                                <div key={label} className="rounded-xl bg-base-200/70 p-3">
                                    <p className="text-2xl font-black text-base-content">{value}</p>
                                    <p className="text-xs text-base-content/55">{label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="glassCard rounded-2xl p-5 md:p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-base-content/50">Ruta creativa</p>
                            <h2 className="mt-1 text-lg font-black text-base-content">{unlocked}/{data.milestones.length} hitos</h2>
                        </div>
                        <Trophy className="text-warning" size={24} />
                    </div>
                    <progress className="progress progress-warning mt-4 w-full" value={unlocked} max={data.milestones.length} />
                    <div className="mt-4 space-y-3">
                        {data.milestones.map((milestone) => (
                            <div key={milestone.id} className={`flex gap-3 ${milestone.unlocked ? "" : "opacity-45"}`}>
                                <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${milestone.unlocked ? "bg-success text-success-content" : "bg-base-300 text-base-content/50"}`}>
                                    {milestone.unlocked ? <Check size={14} /> : <Clock3 size={13} />}
                                </span>
                                <div>
                                    <p className="text-sm font-bold text-base-content">{milestone.label}</p>
                                    <p className="text-xs text-base-content/55">{milestone.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="grid xl:grid-cols-2 gap-4">
                <div className="glassCard rounded-2xl p-5 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-lg font-black text-base-content">Diseños recientes</h2>
                            <p className="text-xs text-base-content/55">Continúa justo donde lo dejaste</p>
                        </div>
                        <Link to="/profile/modelos_creados" className="btn btn-ghost btn-xs gap-1">Ver todos <ArrowRight size={13} /></Link>
                    </div>
                    {data.recentDesigns.length ? (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {data.recentDesigns.map((design) => (
                                <article key={design.id} className="group min-w-0">
                                    <div className="relative aspect-square overflow-hidden rounded-xl bg-base-200 border border-base-300 group-hover:border-primary/40 transition-colors">
                                        {getDesignPreview(design) ? (
                                            <img
                                                src={getDesignPreview(design)}
                                                alt={`Vista previa de ${design.name}`}
                                                className="h-full w-full object-contain p-2 transition-transform group-hover:scale-105"
                                            />
                                        ) : (
                                            <div className="flex h-full items-center justify-center"><Box className="text-base-content/20" size={34} /></div>
                                        )}
                                        {design.thumbnail3d && (
                                            <img
                                                src={design.thumbnail3d}
                                                alt={`Render 3D de ${design.name}`}
                                                className="absolute inset-0 h-full w-full object-contain bg-base-200 p-2
                                                           opacity-0 group-hover:opacity-100 group-focus-within:opacity-100
                                                           transition-opacity duration-200"
                                            />
                                        )}
                                        <div className="absolute inset-0 flex flex-col sm:flex-row items-center justify-center gap-2 bg-black/45 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                                            <Link
                                                to={editorPathForId(design.id)}
                                                className="btn btn-sm btn-circle btn-primary shadow-lg"
                                                aria-label={`Editar ${design.name}`}
                                                title="Editar diseño"
                                            >
                                                <Edit2 size={15} />
                                            </Link>
                                            <Link
                                                to={`/viewer/${design.id}/`}
                                                className="btn btn-sm btn-circle btn-secondary shadow-lg"
                                                aria-label={`Ver ${design.name} en 3D`}
                                                title="Ver en 3D"
                                            >
                                                <Eye size={15} />
                                            </Link>
                                        </div>
                                    </div>
                                    <p className="mt-2 truncate text-sm font-bold text-base-content">{design.name}</p>
                                    <p className="text-[11px] text-base-content/45">{formatDate(design.modifiedAt)}</p>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-xl border border-dashed border-base-300 p-8 text-center">
                            <Box className="mx-auto text-base-content/25" size={34} />
                            <p className="mt-2 text-sm text-base-content/60">Tu primera creación aparecerá aquí.</p>
                        </div>
                    )}
                </div>

                <div className="glassCard rounded-2xl p-5 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-lg font-black text-base-content">Actividad reciente</h2>
                            <p className="text-xs text-base-content/55">Cambios en diseños, solicitudes y pedidos</p>
                        </div>
                        <button className="btn btn-ghost btn-xs btn-circle" onClick={fetchDashboard} title="Actualizar"><RefreshCw size={14} /></button>
                    </div>
                    {data.activity.length ? (
                        <div className="space-y-1">
                            {data.activity.map((item) => {
                                const Icon = TYPE_ICON[item.type] || ShoppingBag;
                                return (
                                    <Link key={item.id} to={item.link} className="flex items-center gap-3 rounded-xl p-2.5 hover:bg-base-200 transition-colors">
                                        <span className="rounded-lg bg-base-200 p-2 text-base-content/65"><Icon size={16} /></span>
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-bold text-base-content">{item.title}</p>
                                            <p className="truncate text-xs text-base-content/55">{item.status}</p>
                                        </div>
                                        <time className="hidden sm:block text-[11px] text-base-content/40">{formatDate(item.date)}</time>
                                    </Link>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="py-10 text-center text-sm text-base-content/50">Aún no hay actividad que mostrar.</p>
                    )}
                </div>
            </section>
        </div>
    );
}
